// src/pages/SyncPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify'; // Menggunakan react-toastify
import './SyncPage.css';

function SyncPage() {
  const [settings, setSettings] = useState({ interval_days: 1, run_hour: 3, is_enabled: false });
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [realtimeLogs, setRealtimeLogs] = useState("Loading real-time logs...");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [logLines, setLogLines] = useState(100);
  const { authFetch } = useAuth(); // Hanya butuh authFetch

  // State untuk Sync Scrapper Manual
  const [scraperFailedCount, setScraperFailedCount] = useState(0);
  const [scraperNoDataCount, setScraperNoDataCount] = useState(0);
  const [scraperSyncResult, setScraperSyncResult] = useState(null);
  const [scraperSyncHistory, setScraperSyncHistory] = useState([]);
  const [isLoadingScraperHistory, setIsLoadingScraperHistory] = useState(false);
  const [showScraperHistoryModal, setShowScraperHistoryModal] = useState(false);
  const [scraperHistoryPage, setScraperHistoryPage] = useState(1);
  const [expandedScraperLogId, setExpandedScraperLogId] = useState(null);
  const scraperHistoryPerPage = 5;

  // Fungsi untuk memuat data awal (pengaturan)
  const fetchData = async () => {
    try {
      const settingsResponse = await authFetch('/api/scheduler/settings');
      const settingsData = await settingsResponse.json();
      if (settingsData && !settingsData.error) {
        setSettings(settingsData);
      }
    } catch (error) {
      console.error("Gagal mengambil pengaturan:", error);
    }
  };

  // Fungsi untuk memuat real-time logs
  const fetchRealtimeLogs = async () => {
    try {
      const response = await authFetch(`/api/realtime-logs?lines=${logLines}`);
      const data = await response.json();
      
      if (data.success) {
        setRealtimeLogs(data.logs || "No logs available.");
      } else {
        setRealtimeLogs(`Error: ${data.error}`);
      }
    } catch (error) {
      console.error("Gagal mengambil real-time logs:", error);
      setRealtimeLogs(`Failed to fetch logs: ${error.message}`);
    }
  };

  // useEffect berjalan HANYA SEKALI saat komponen dimuat
  useEffect(() => {
    fetchData();
    fetchRealtimeLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh real-time logs
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchRealtimeLogs();
      }, 3000); // Refresh every 3 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, logLines]);

  // State untuk filter tanggal
  const today = new Date();
  const nineDaysAgo = new Date();
  nineDaysAgo.setDate(today.getDate() - 9);
  
  const [dateRange, setDateRange] = useState({
    start: nineDaysAgo.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10)
  });

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const handleSettingChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseInt(value, 10)
    }));
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      const response = await authFetch('/api/scheduler/settings', {
        method: 'POST',
        body: JSON.stringify(settings)
      });
      if (!response.ok) throw new Error('Gagal menyimpan pengaturan');
      toast.success('Pengaturan berhasil disimpan!');
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchScraperSyncHistory = async () => {
    setIsLoadingScraperHistory(true);
    try {
      const response = await authFetch('/api/sync-history');
      const data = await response.json();
      if (Array.isArray(data)) {
        setScraperSyncHistory(data);
      }
    } catch (error) {
      console.error("Gagal mengambil riwayat sync scraper:", error);
    } finally {
      setIsLoadingScraperHistory(false);
    }
  };

  const pollScraperStatus = () => {
    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await authFetch('/api/sync-status');
        const statusData = await statusRes.json();
        setScraperFailedCount(statusData.failed_count || 0);
        setScraperNoDataCount(statusData.nodata_count || 0);
        setScraperSyncResult(statusData.result);

        if (!statusData.running) {
          clearInterval(pollInterval);
          setIsSyncing(false);
          fetchScraperSyncHistory();
          if (statusData.result && (statusData.result.status === 'success' || statusData.result.status === 'partial_success')) {
            const msg = statusData.result.message || 'Sync selesai!';
            if (statusData.failed_count > 0) {
              toast.warn(`${msg} (${statusData.failed_count} PGA gagal — silakan Retry)`);
            } else {
              toast.success(msg);
            }
          } else {
            toast.error(statusData.result?.message || statusData.error || 'Sync gagal');
          }
        }
      } catch (err) {
        clearInterval(pollInterval);
        setIsSyncing(false);
        toast.error(`Poll error: ${err.message}`);
      }
    }, 5000);
  };

  const handleManualSync = async () => {
    if (!window.confirm('Jalankan sinkronisasi manual sekarang?')) return;
    setIsSyncing(true);
    setScraperFailedCount(0);
    setScraperNoDataCount(0);
    setScraperSyncResult(null);
    try {
      const response = await authFetch('/api/trigger-sync', { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          start_date: dateRange.start, 
          end_date: dateRange.end 
        })
      });
      
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Sync gagal dimulai');
        setIsSyncing(false);
        return;
      }
      toast.info(data.message || 'Sinkronisasi manual dimulai...');
      pollScraperStatus();

    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncing(false);
    }
  };

  const handleRetryFailedScraper = async () => {
    if (!window.confirm(`Retry ${scraperFailedCount} PGA yang gagal?`)) return;
    setIsSyncing(true);
    try {
      const response = await authFetch('/api/retry-failed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncing(false);
        return;
      }
      toast.info(data.message || 'Retry dimulai...');
      pollScraperStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncing(false);
    }
  };

  const handleRetryNoDataScraper = async () => {
    if (!window.confirm(`Retry ${scraperNoDataCount} PGA yang kosong?`)) return;
    setIsSyncing(true);
    try {
      const response = await authFetch('/api/retry-nodata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncing(false);
        return;
      }
      toast.info(data.message || 'Retry dimulai...');
      pollScraperStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncing(false);
    }
  };

  const handleRetryLogFailedScraper = async (logId, count) => {
    if (!window.confirm(`Retry ${count} PGA gagal dari Run #${logId}?`)) return;
    setIsSyncing(true);
    try {
      const response = await authFetch(`/api/retry-log-failed/${logId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncing(false);
        return;
      }
      toast.info(data.message || 'Retry dimulai...');
      setShowScraperHistoryModal(false);
      pollScraperStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncing(false);
    }
  };

  const handleRetryLogNoDataScraper = async (logId, count) => {
    if (!window.confirm(`Retry ${count} PGA kosong dari Run #${logId}?`)) return;
    setIsSyncing(true);
    try {
      const response = await authFetch(`/api/retry-log-nodata/${logId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncing(false);
        return;
      }
      toast.info(data.message || 'Retry dimulai...');
      setShowScraperHistoryModal(false);
      pollScraperStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncing(false);
    }
  };

  // State untuk Sync Top Issuer
  const [topIssuerDateRange, setTopIssuerDateRange] = useState({
    start: nineDaysAgo.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10)
  });
  const [isSyncingTopIssuer, setIsSyncingTopIssuer] = useState(false);
  const [topIssuerFailedCount, setTopIssuerFailedCount] = useState(0);
  const [topIssuerNoDataCount, setTopIssuerNoDataCount] = useState(0);
  const [topIssuerSyncResult, setTopIssuerSyncResult] = useState(null);
  const [topIssuerSyncHistory, setTopIssuerSyncHistory] = useState([]);
  const [isLoadingTopIssuerHistory, setIsLoadingTopIssuerHistory] = useState(false);
  const [showTopIssuerHistoryModal, setShowTopIssuerHistoryModal] = useState(false);
  const [topIssuerHistoryPage, setTopIssuerHistoryPage] = useState(1);
  const [expandedTopIssuerLogId, setExpandedTopIssuerLogId] = useState(null);
  const topIssuerHistoryPerPage = 5;

  const handleTopIssuerDateChange = (e) => {
    const { name, value } = e.target;
    setTopIssuerDateRange(prev => ({ ...prev, [name]: value }));
  };

  const fetchTopIssuerSyncHistory = async () => {
    setIsLoadingTopIssuerHistory(true);
    try {
      const response = await authFetch('/api/top-issuer/sync-history');
      const data = await response.json();
      if (Array.isArray(data)) {
        setTopIssuerSyncHistory(data);
      }
    } catch (error) {
      console.error("Gagal mengambil riwayat sync top issuer:", error);
    } finally {
      setIsLoadingTopIssuerHistory(false);
    }
  };

  const pollTopIssuerStatus = () => {
    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await authFetch('/api/top-issuer/sync-status');
        const statusData = await statusRes.json();
        setTopIssuerFailedCount(statusData.failed_count || 0);
        setTopIssuerNoDataCount(statusData.nodata_count || 0);
        setTopIssuerSyncResult(statusData.result);

        if (!statusData.running) {
          clearInterval(pollInterval);
          setIsSyncingTopIssuer(false);
          fetchTopIssuerSyncHistory();
          if (statusData.result && statusData.result.status === 'success') {
            toast.success(statusData.result.message || 'Sync selesai!');
          } else {
            toast.error(statusData.result?.message || 'Sync gagal');
          }
        }
      } catch (err) {
        clearInterval(pollInterval);
        setIsSyncingTopIssuer(false);
        toast.error(`Poll error: ${err.message}`);
      }
    }, 3000);
  };

  const handleSyncTopIssuer = async () => {
    if (!window.confirm('Jalankan sinkronisasi Top Issuer via API sekarang?')) return;
    setIsSyncingTopIssuer(true);
    setTopIssuerFailedCount(0);
    setTopIssuerNoDataCount(0);
    setTopIssuerSyncResult(null);
    try {
      const response = await authFetch('/api/top-issuer/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: topIssuerDateRange.start,
          end_date: topIssuerDateRange.end
        })
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Sync gagal dimulai');
        setIsSyncingTopIssuer(false);
        return;
      }
      toast.info(data.message || 'Sync dimulai...');
      pollTopIssuerStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingTopIssuer(false);
    }
  };

  const handleRetryFailedTopIssuer = async () => {
    if (!window.confirm(`Retry ${topIssuerFailedCount} PGA yang gagal?`)) return;
    setIsSyncingTopIssuer(true);
    try {
      const response = await authFetch('/api/top-issuer/retry-failed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingTopIssuer(false);
        return;
      }
      toast.info(data.message || 'Retry PGA gagal dimulai...');
      pollTopIssuerStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingTopIssuer(false);
    }
  };

  const handleRetryNoDataTopIssuer = async () => {
    if (!window.confirm(`Retry ${topIssuerNoDataCount} PGA kosong (no data)?`)) return;
    setIsSyncingTopIssuer(true);
    try {
      const response = await authFetch('/api/top-issuer/retry-nodata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingTopIssuer(false);
        return;
      }
      toast.info(data.message || 'Retry PGA kosong dimulai...');
      pollTopIssuerStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingTopIssuer(false);
    }
  };

  const handleRetryTopIssuerLogFailed = async (logId, count) => {
    if (!window.confirm(`Retry ${count} PGA gagal untuk log #${logId}?`)) return;
    setIsSyncingTopIssuer(true);
    try {
      const response = await authFetch(`/api/top-issuer/retry-log-failed/${logId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingTopIssuer(false);
        return;
      }
      toast.info(data.message || 'Retry PGA gagal dimulai...');
      pollTopIssuerStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingTopIssuer(false);
    }
  };

  const handleRetryTopIssuerLogNoData = async (logId, count) => {
    if (!window.confirm(`Retry ${count} PGA kosong (no data) untuk log #${logId}?`)) return;
    setIsSyncingTopIssuer(true);
    try {
      const response = await authFetch(`/api/top-issuer/retry-log-nodata/${logId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingTopIssuer(false);
        return;
      }
      toast.info(data.message || 'Retry PGA kosong dimulai...');
      pollTopIssuerStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingTopIssuer(false);
    }
  };

  // State untuk Sync Transaction Detail
  const [detailDateRange, setDetailDateRange] = useState({
    start: nineDaysAgo.toISOString().slice(0, 10),
    end: today.toISOString().slice(0, 10)
  });
  const [isSyncingDetail, setIsSyncingDetail] = useState(false);
  const [detailFailedCount, setDetailFailedCount] = useState(0);
  const [detailNoDataCount, setDetailNoDataCount] = useState(0);
  const [detailSyncResult, setDetailSyncResult] = useState(null);
  const [detailSyncHistory, setDetailSyncHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const historyPerPage = 5;

  const handleDetailDateChange = (e) => {
    const { name, value } = e.target;
    setDetailDateRange(prev => ({ ...prev, [name]: value }));
  };

  const fetchSyncHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await authFetch('/api/transaction-detail/sync-history');
      const data = await response.json();
      if (Array.isArray(data)) {
        setDetailSyncHistory(data);
      }
    } catch (error) {
      console.error("Gagal mengambil riwayat sync detail:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const pollDetailStatus = () => {
    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await authFetch('/api/transaction-detail/sync-status');
        const statusData = await statusRes.json();
        setDetailFailedCount(statusData.failed_count || 0);
        setDetailNoDataCount(statusData.nodata_count || 0);
        setDetailSyncResult(statusData.result);

        if (!statusData.running) {
          clearInterval(pollInterval);
          setIsSyncingDetail(false);
          fetchSyncHistory();
          if (statusData.result && statusData.result.status === 'success') {
            const resultMsg = statusData.result.message || 'Sync detail selesai!';
            toast.success(resultMsg);
          } else {
            toast.error(statusData.result?.message || 'Sync detail gagal');
          }
        }
      } catch (err) {
        clearInterval(pollInterval);
        setIsSyncingDetail(false);
        toast.error(`Poll error: ${err.message}`);
      }
    }, 3000);
  };

  const handleSyncDetail = async () => {
    if (!window.confirm('Jalankan sinkronisasi Transaction Detail via API sekarang?')) return;
    setIsSyncingDetail(true);
    setDetailFailedCount(0);
    setDetailNoDataCount(0);
    setDetailSyncResult(null);
    try {
      const response = await authFetch('/api/transaction-detail/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: detailDateRange.start,
          end_date: detailDateRange.end
        })
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Sync detail gagal dimulai');
        setIsSyncingDetail(false);
        return;
      }
      toast.info(data.message || 'Sync detail dimulai...');
      pollDetailStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingDetail(false);
    }
  };

  const handleRetryFailedDetail = async () => {
    if (!window.confirm(`Retry ${detailFailedCount} PGA yang gagal?`)) return;
    setIsSyncingDetail(true);
    try {
      const response = await authFetch('/api/transaction-detail/retry-failed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingDetail(false);
        return;
      }
      toast.info(data.message || 'Retry PGA gagal dimulai...');
      pollDetailStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingDetail(false);
    }
  };

  const handleRetryNoDataDetail = async () => {
    if (!window.confirm(`Retry ${detailNoDataCount} PGA kosong (no data)?`)) return;
    setIsSyncingDetail(true);
    try {
      const response = await authFetch('/api/transaction-detail/retry-nodata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingDetail(false);
        return;
      }
      toast.info(data.message || 'Retry PGA kosong dimulai...');
      pollDetailStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingDetail(false);
    }
  };

  const handleRetryLogFailed = async (logId, count) => {
    if (!window.confirm(`Retry ${count} PGA gagal untuk log #${logId}?`)) return;
    setIsSyncingDetail(true);
    try {
      const response = await authFetch(`/api/transaction-detail/retry-log-failed/${logId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingDetail(false);
        return;
      }
      toast.info(data.message || 'Retry PGA gagal dimulai...');
      pollDetailStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingDetail(false);
    }
  };

  const handleRetryLogNoData = async (logId, count) => {
    if (!window.confirm(`Retry ${count} PGA kosong (no data) untuk log #${logId}?`)) return;
    setIsSyncingDetail(true);
    try {
      const response = await authFetch(`/api/transaction-detail/retry-log-nodata/${logId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingDetail(false);
        return;
      }
      toast.info(data.message || 'Retry PGA kosong dimulai...');
      pollDetailStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingDetail(false);
    }
  };

  // State untuk Sync Merchant Report
  const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const [merchantMonth, setMerchantMonth] = useState(currentMonth);
  const [merchantType, setMerchantType] = useState('all');
  const [isSyncingMerchant, setIsSyncingMerchant] = useState(false);
  const [merchantFailedCount, setMerchantFailedCount] = useState(0);
  const [pgaList, setPgaList] = useState([]);
  const [selectedPgaIds, setSelectedPgaIds] = useState([]);
  const [showPgaPicker, setShowPgaPicker] = useState(false);
  const [clearExisting, setClearExisting] = useState(false);
  const [merchantSyncHistory, setMerchantSyncHistory] = useState([]);
  const [isLoadingMerchantHistory, setIsLoadingMerchantHistory] = useState(false);
  const [showMerchantHistoryModal, setShowMerchantHistoryModal] = useState(false);
  const [merchantHistoryPage, setMerchantHistoryPage] = useState(1);
  const [expandedMerchantLogId, setExpandedMerchantLogId] = useState(null);
  const merchantHistoryPerPage = 5;

  // Load PGA list and check if sync is running on mount
  useEffect(() => {
    const loadPgaList = async () => {
      try {
        const res = await authFetch('/api/merchant-pga');
        const data = await res.json();
        setPgaList(data.filter(p => p.is_active));
      } catch (e) { /* ignore */ }
    };
    const checkMerchantStatus = async () => {
      try {
        const res = await authFetch('/api/merchant-report/sync-status');
        const data = await res.json();
        setMerchantFailedCount(data.failed_count || 0);
        if (data.running) {
          setIsSyncingMerchant(true);
          pollMerchantStatus();
        }
      } catch (e) { /* ignore */ }
    };
    const checkDetailStatus = async () => {
      try {
        const res = await authFetch('/api/transaction-detail/sync-status');
        const data = await res.json();
        setDetailFailedCount(data.failed_count || 0);
        setDetailNoDataCount(data.nodata_count || 0);
        setDetailSyncResult(data.result);
        if (data.running) {
          setIsSyncingDetail(true);
          pollDetailStatus();
        }
      } catch (e) { /* ignore */ }
    };
    const checkTopIssuerStatus = async () => {
      try {
        const res = await authFetch('/api/top-issuer/sync-status');
        const data = await res.json();
        setTopIssuerFailedCount(data.failed_count || 0);
        setTopIssuerNoDataCount(data.nodata_count || 0);
        setTopIssuerSyncResult(data.result);
        if (data.running) {
          setIsSyncingTopIssuer(true);
          pollTopIssuerStatus();
        }
      } catch (e) { /* ignore */ }
    };
    const checkScraperStatus = async () => {
      try {
        const res = await authFetch('/api/sync-status');
        const data = await res.json();
        setScraperFailedCount(data.failed_count || 0);
        setScraperNoDataCount(data.nodata_count || 0);
        setScraperSyncResult(data.result);
        if (data.running) {
          setIsSyncing(true);
          pollScraperStatus();
        }
      } catch (e) { /* ignore */ }
    };
    loadPgaList();
    checkMerchantStatus();
    checkDetailStatus();
    checkTopIssuerStatus();
    checkScraperStatus();
    fetchScraperSyncHistory();
    fetchSyncHistory();
    fetchTopIssuerSyncHistory();
    fetchMerchantSyncHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMerchantSyncHistory = async () => {
    setIsLoadingMerchantHistory(true);
    try {
      const response = await authFetch('/api/merchant-report/sync-history');
      const data = await response.json();
      if (Array.isArray(data)) {
        setMerchantSyncHistory(data);
      }
    } catch (error) {
      console.error("Gagal mengambil riwayat sync merchant:", error);
    } finally {
      setIsLoadingMerchantHistory(false);
    }
  };

  const handleRetryLogFailedMerchant = async (logId, count) => {
    if (!window.confirm(`Retry ${count} PGA gagal dari Run #${logId}?`)) return;
    setIsSyncingMerchant(true);
    try {
      const response = await authFetch(`/api/merchant-report/retry-log-failed/${logId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingMerchant(false);
        return;
      }
      toast.info(data.message || 'Retry dimulai...');
      setShowMerchantHistoryModal(false);
      pollMerchantStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingMerchant(false);
    }
  };

  const handleRetryLogNoDataMerchant = async (logId, count) => {
    if (!window.confirm(`Retry ${count} PGA kosong dari Run #${logId}?`)) return;
    setIsSyncingMerchant(true);
    try {
      const response = await authFetch(`/api/merchant-report/retry-log-nodata/${logId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingMerchant(false);
        return;
      }
      toast.info(data.message || 'Retry dimulai...');
      setShowMerchantHistoryModal(false);
      pollMerchantStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingMerchant(false);
    }
  };

  const pollMerchantStatus = () => {
    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await authFetch('/api/merchant-report/sync-status');
        const statusData = await statusRes.json();
        if (!statusData.running) {
          clearInterval(pollInterval);
          setIsSyncingMerchant(false);
          setMerchantFailedCount(statusData.failed_count || 0);
          fetchMerchantSyncHistory();
          if (statusData.result && statusData.result.status === 'success') {
            const msg = statusData.result.message || 'Sync selesai!';
            if (statusData.failed_count > 0) {
              toast.warn(`${msg} (${statusData.failed_count} PGA gagal — bisa Retry)`);
            } else {
              toast.success(msg);
            }
          } else {
            toast.error(statusData.result?.message || 'Sync gagal');
          }
        }
      } catch (err) {
        clearInterval(pollInterval);
        setIsSyncingMerchant(false);
        toast.error(`Poll error: ${err.message}`);
      }
    }, 5000);
  };

  const handleSyncMerchant = async (isH30) => {
    const label = isH30 ? 'H+30' : 'reguler';
    if (!window.confirm(`Jalankan Sync Merchant Report (${label}) untuk ${merchantMonth}?`)) return;
    setIsSyncingMerchant(true);
    setMerchantFailedCount(0);
    try {
      const period = `${merchantMonth}-01`;
      const response = await authFetch('/api/merchant-report/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period, is_h30: isH30, type: merchantType, clear_existing: clearExisting })
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Sync gagal dimulai');
        setIsSyncingMerchant(false);
        return;
      }
      toast.info(data.message || 'Sync dimulai...');
      pollMerchantStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingMerchant(false);
    }
  };

  const handleRetryFailed = async () => {
    if (!window.confirm(`Retry ${merchantFailedCount} PGA yang gagal?`)) return;
    setIsSyncingMerchant(true);
    try {
      const response = await authFetch('/api/merchant-report/retry-failed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Retry gagal dimulai');
        setIsSyncingMerchant(false);
        return;
      }
      toast.info(data.message || 'Retry dimulai...');
      pollMerchantStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingMerchant(false);
    }
  };

  const handleSyncSelectedPga = async (isH30) => {
    if (selectedPgaIds.length === 0) {
      toast.error('Pilih minimal 1 PGA');
      return;
    }
    const label = isH30 ? 'H+30' : 'reguler';
    const names = pgaList.filter(p => selectedPgaIds.includes(p.id)).map(p => p.pga_name).join(', ');
    if (!window.confirm(`Sync ${label} untuk ${selectedPgaIds.length} PGA (${names}) di ${merchantMonth}?`)) return;
    setIsSyncingMerchant(true);
    try {
      const period = `${merchantMonth}-01`;
      const response = await authFetch('/api/merchant-report/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period, is_h30: isH30, type: 'all', pga_ids: selectedPgaIds, clear_existing: clearExisting })
      });
      const data = await response.json();
      if (data.status !== 'success') {
        toast.error(data.message || 'Sync gagal dimulai');
        setIsSyncingMerchant(false);
        return;
      }
      toast.info(data.message || 'Sync dimulai...');
      pollMerchantStatus();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      setIsSyncingMerchant(false);
    }
  };

  const togglePgaSelection = (id) => {
    setSelectedPgaIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllPgaByType = (type) => {
    const ids = pgaList.filter(p => p.type === type).map(p => p.id);
    setSelectedPgaIds(prev => {
      const existing = new Set(prev);
      const allSelected = ids.every(id => existing.has(id));
      if (allSelected) {
        return prev.filter(id => !ids.includes(id));
      } else {
        return [...new Set([...prev, ...ids])];
      }
    });
  };

  return (
    <div className="sync-page">
      <h1>Sync Data</h1>
      
      <div className="sync-section">
        <h2>Manual Sync (Scrapper)</h2>
        <p>Jalankan proses sinkronisasi data via scraping secara manual kapan pun diperlukan.</p>
        
        <div className="manual-sync-filters">
          <label>Date Range:</label>
          <input type="date" name="start" value={dateRange.start} onChange={handleDateChange} />
          <span>-</span>
          <input type="date" name="end" value={dateRange.end} onChange={handleDateChange} />
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={handleManualSync} disabled={isSyncing} className="sync-button">
            {isSyncing ? 'Syncing...' : 'Run Sync Now'}
          </button>
          {scraperFailedCount > 0 && !isSyncing && (
            <button onClick={handleRetryFailedScraper} className="sync-button" style={{ background: '#dc3545' }}>
              Retry {scraperFailedCount} PGA Gagal
            </button>
          )}

        </div>

        {scraperSyncResult && (
          <div className="sync-result-summary" style={{ marginTop: '10px', fontSize: '14px', color: '#555' }}>
            <strong>Hasil terakhir:</strong> {scraperSyncResult.message}
            {(scraperFailedCount > 0 || scraperNoDataCount > 0) && (
              <span style={{ marginLeft: '10px', fontWeight: '600', color: '#dc3545' }}>
                ({scraperFailedCount} gagal, {scraperNoDataCount} kosong)
              </span>
            )}
          </div>
        )}

        {/* Button to Open Scraper Sync History Modal */}
        <div style={{ marginTop: '15px', display: 'flex', gap: '8px' }}>
          <button 
            onClick={() => {
              setShowScraperHistoryModal(true);
              setScraperHistoryPage(1);
              fetchScraperSyncHistory();
            }} 
            className="sync-button" 
            style={{ background: '#17a2b8', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            📋 Lihat Riwayat Sync Scrapper
          </button>
        </div>

        {/* Scraper Sync History Modal Overlay */}
        {showScraperHistoryModal && (
          <div className="sync-modal-overlay" onClick={() => setShowScraperHistoryModal(false)}>
            <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sync-modal-header">
                <h3>Riwayat Sinkronisasi Manual Scrapper</h3>
                <button className="sync-modal-close-btn" onClick={() => setShowScraperHistoryModal(false)}>&times;</button>
              </div>
              <div className="sync-modal-body">
                {isLoadingScraperHistory ? (
                  <p style={{ color: '#666', fontSize: '14px', textAlign: 'center', padding: '20px' }}>Memuat riwayat...</p>
                ) : scraperSyncHistory.length === 0 ? (
                  <p style={{ color: '#666', fontSize: '14px', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>Belum ada riwayat sinkronisasi scrapper.</p>
                ) : (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="sync-history-table">
                        <thead>
                          <tr>
                            <th>ID Run</th>
                            <th>Waktu Sync</th>
                            <th>Periode</th>
                            <th>Status</th>
                            <th>Hasil Sync</th>
                            <th>Catatan</th>
                            <th>Aksi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scraperSyncHistory.slice((scraperHistoryPage - 1) * scraperHistoryPerPage, scraperHistoryPage * scraperHistoryPerPage).map((log) => (
                            <React.Fragment key={log.id}>
                              <tr>
                                <td>#{log.id}</td>
                                <td>{new Date(log.created_at).toLocaleString('id-ID')}</td>
                                <td>{log.start_date} s/d {log.end_date}</td>
                                <td>
                                  <span className={`status-badge status-${log.status}`}>
                                    {log.status === 'running' ? 'Running' : log.status === 'success' ? 'Sukses' : 'Gagal'}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ fontSize: '13px' }}>
                                    <span style={{ color: '#28a745', fontWeight: 'bold' }} title="Inserted Count">+{log.inserted_count}</span> |{' '}
                                    <span style={{ color: '#007bff', fontWeight: 'bold' }} title="Updated Count">~{log.updated_count}</span> |{' '}
                                    <span style={{ color: '#6c757d', fontWeight: 'bold' }} title="Skipped Count">={log.skipped_count}</span>
                                    {(log.failed_count > 0 || log.nodata_count > 0) && (
                                      <div style={{ marginTop: '4px', fontSize: '12px' }}>
                                        {log.failed_count > 0 && <span style={{ color: '#dc3545', fontWeight: '600', marginRight: '8px' }}>{log.failed_count} Gagal</span>}
                                        {log.nodata_count > 0 && <span style={{ color: '#ffc107', fontWeight: '600' }}>{log.nodata_count} Kosong</span>}
                                        <div style={{ marginTop: '4px' }}>
                                          <button 
                                            onClick={() => setExpandedScraperLogId(expandedScraperLogId === log.id ? null : log.id)} 
                                            style={{
                                              background: 'none', border: 'none', color: '#007bff', padding: 0, fontSize: '11px',
                                              textDecoration: 'underline', cursor: 'pointer', fontWeight: '600'
                                            }}
                                          >
                                            {expandedScraperLogId === log.id ? 'Sembunyikan Detail' : 'Lihat Detail URL'}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td style={{ fontSize: '13px', maxWidth: '200px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {log.message || '-'}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    {log.failed_count > 0 && (
                                      <button 
                                        onClick={() => handleRetryLogFailedScraper(log.id, log.failed_count)} 
                                        disabled={isSyncing} 
                                        className="action-btn btn-danger"
                                        title="Retry hanya PGA yang gagal pada run ini"
                                      >
                                        Retry Gagal
                                      </button>
                                    )}
                                    {log.nodata_count > 0 && (
                                      <button 
                                        onClick={() => handleRetryLogNoDataScraper(log.id, log.nodata_count)} 
                                        disabled={isSyncing} 
                                        className="action-btn btn-warning"
                                        title="Retry hanya PGA yang kosong (no data) pada run ini"
                                      >
                                        Retry Kosong
                                      </button>
                                    )}
                                    {!log.failed_count && !log.nodata_count && <span>-</span>}
                                  </div>
                                </td>
                              </tr>
                              {expandedScraperLogId === log.id && (
                                <tr className="expanded-details-row">
                                  <td colSpan="7" style={{ backgroundColor: '#fdfefe', padding: '15px', borderBottom: '1px solid #dee2e6' }}>
                                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                      {log.failed_items && log.failed_items.length > 0 && (
                                        <div style={{ flex: 1, minWidth: '250px' }}>
                                          <h4 style={{ color: '#dc3545', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '600', textAlign: 'left' }}>
                                            🔴 List PGA Gagal ({log.failed_items.length}):
                                          </h4>
                                          <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#fff', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '8px' }}>
                                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#721c24', textAlign: 'left' }}>
                                              {log.failed_items.map((item, idx) => (
                                                <li key={idx} style={{ marginBottom: '4px' }}>
                                                  <strong>{item.pga_name}</strong> ({item.vendor} - {item.report_type}) - <a href={item.pga_url} target="_blank" rel="noopener noreferrer">{item.pga_url}</a>
                                                  {item.reason && <span style={{ color: '#888', fontStyle: 'italic', marginLeft: '4px' }}>- {item.reason}</span>}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        </div>
                                      )}
                                      {log.nodata_items && log.nodata_items.length > 0 && (
                                        <div style={{ flex: 1, minWidth: '250px' }}>
                                          <h4 style={{ color: '#856404', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '600', textAlign: 'left' }}>
                                            🟡 List PGA Kosong/No Data ({log.nodata_items.length}):
                                          </h4>
                                          <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#fff', border: '1px solid #ffeeba', borderRadius: '4px', padding: '8px' }}>
                                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#856404', textAlign: 'left' }}>
                                              {log.nodata_items.map((item, idx) => (
                                                <li key={idx} style={{ marginBottom: '4px' }}>
                                                  <strong>{item.pga_name}</strong> ({item.vendor} - {item.report_type}) - <a href={item.pga_url} target="_blank" rel="noopener noreferrer">{item.pga_url}</a>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination Controls */}
                    {Math.ceil(scraperSyncHistory.length / scraperHistoryPerPage) > 1 && (
                      <div className="sync-pagination">
                        <button 
                          className="pagination-btn"
                          disabled={scraperHistoryPage === 1}
                          onClick={() => setScraperHistoryPage(prev => prev - 1)}
                        >
                          &laquo; Prev
                        </button>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                          Halaman {scraperHistoryPage} dari {Math.ceil(scraperSyncHistory.length / scraperHistoryPerPage)}
                        </span>
                        <button 
                          className="pagination-btn"
                          disabled={scraperHistoryPage === Math.ceil(scraperSyncHistory.length / scraperHistoryPerPage)}
                          onClick={() => setScraperHistoryPage(prev => prev + 1)}
                        >
                          Next &raquo;
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="sync-modal-footer">
                <button className="sync-button" style={{ background: '#6c757d', padding: '8px 16px', fontSize: '14px' }} onClick={() => setShowScraperHistoryModal(false)}>Tutup</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="sync-section">
        <h2>Sync Top Issuer (API)</h2>
        <p>Tarik data Top Issuer dari API <code>stat_top_issuer</code>. Pastikan API Settings sudah dikonfigurasi di Master Data → Master Data API → Global API Settings.</p>
        
        <div className="manual-sync-filters">
          <label>Date Range:</label>
          <input type="date" name="start" value={topIssuerDateRange.start} onChange={handleTopIssuerDateChange} />
          <span>-</span>
          <input type="date" name="end" value={topIssuerDateRange.end} onChange={handleTopIssuerDateChange} />
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={handleSyncTopIssuer} disabled={isSyncingTopIssuer} className="sync-button api-sync-button">
            {isSyncingTopIssuer ? 'Syncing Top Issuer...' : 'Sync Top Issuer'}
          </button>
          {topIssuerFailedCount > 0 && !isSyncingTopIssuer && (
            <button onClick={handleRetryFailedTopIssuer} className="sync-button" style={{ background: '#dc3545' }}>
              Retry {topIssuerFailedCount} PGA Gagal
            </button>
          )}

        </div>

        {topIssuerSyncResult && (
          <div className="sync-result-summary" style={{ marginTop: '10px', fontSize: '14px', color: '#555' }}>
            <strong>Hasil terakhir:</strong> {topIssuerSyncResult.message}
            {(topIssuerFailedCount > 0 || topIssuerNoDataCount > 0) && (
              <span style={{ marginLeft: '10px', fontWeight: '600', color: '#dc3545' }}>
                ({topIssuerFailedCount} gagal, {topIssuerNoDataCount} kosong)
              </span>
            )}
          </div>
        )}

        {/* Button to Open Top Issuer Sync History Modal */}
        <div style={{ marginTop: '15px', display: 'flex', gap: '8px' }}>
          <button 
            onClick={() => {
              setShowTopIssuerHistoryModal(true);
              setTopIssuerHistoryPage(1);
              fetchTopIssuerSyncHistory();
            }} 
            className="sync-button" 
            style={{ background: '#17a2b8', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            📋 Lihat Riwayat Sync Top Issuer
          </button>
        </div>

        {/* Top Issuer Sync History Modal Overlay */}
        {showTopIssuerHistoryModal && (
          <div className="sync-modal-overlay" onClick={() => setShowTopIssuerHistoryModal(false)}>
            <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sync-modal-header">
                <h3>Riwayat Sinkronisasi Top Issuer</h3>
                <button className="sync-modal-close-btn" onClick={() => setShowTopIssuerHistoryModal(false)}>&times;</button>
              </div>
              <div className="sync-modal-body">
                {isLoadingTopIssuerHistory ? (
                  <p style={{ color: '#666', fontSize: '14px', textAlign: 'center', padding: '20px' }}>Memuat riwayat...</p>
                ) : topIssuerSyncHistory.length === 0 ? (
                  <p style={{ color: '#666', fontSize: '14px', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>Belum ada riwayat sinkronisasi top issuer.</p>
                ) : (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="sync-history-table">
                        <thead>
                          <tr>
                            <th>ID Run</th>
                            <th>Waktu Sync</th>
                            <th>Periode</th>
                            <th>Status</th>
                            <th>Hasil Sync</th>
                            <th>Catatan</th>
                            <th>Aksi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topIssuerSyncHistory.slice((topIssuerHistoryPage - 1) * topIssuerHistoryPerPage, topIssuerHistoryPage * topIssuerHistoryPerPage).map((log) => (
                            <React.Fragment key={log.id}>
                              <tr>
                                <td>#{log.id}</td>
                                <td>{new Date(log.created_at).toLocaleString('id-ID')}</td>
                                <td>{log.start_date} s/d {log.end_date}</td>
                                <td>
                                  <span className={`status-badge status-${log.status}`}>
                                    {log.status === 'running' ? 'Running' : log.status === 'success' ? 'Sukses' : 'Gagal'}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ fontSize: '13px' }}>
                                    <span style={{ color: '#28a745', fontWeight: 'bold' }} title="Inserted Count">+{log.inserted_count}</span> |{' '}
                                    <span style={{ color: '#007bff', fontWeight: 'bold' }} title="Updated Count">~{log.updated_count}</span> |{' '}
                                    <span style={{ color: '#6c757d', fontWeight: 'bold' }} title="Skipped Count">={log.skipped_count}</span>
                                    {(log.failed_count > 0 || log.nodata_count > 0) && (
                                      <div style={{ marginTop: '4px', fontSize: '12px' }}>
                                        {log.failed_count > 0 && <span style={{ color: '#dc3545', fontWeight: '600', marginRight: '8px' }}>{log.failed_count} Gagal</span>}
                                        {log.nodata_count > 0 && <span style={{ color: '#ffc107', fontWeight: '600' }}>{log.nodata_count} Kosong</span>}
                                        <div style={{ marginTop: '4px' }}>
                                          <button 
                                            onClick={() => setExpandedTopIssuerLogId(expandedTopIssuerLogId === log.id ? null : log.id)} 
                                            style={{
                                              background: 'none', border: 'none', color: '#007bff', padding: 0, fontSize: '11px',
                                              textDecoration: 'underline', cursor: 'pointer', fontWeight: '600'
                                            }}
                                          >
                                            {expandedTopIssuerLogId === log.id ? 'Sembunyikan Detail' : 'Lihat Detail URL'}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td style={{ fontSize: '13px', maxWidth: '200px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {log.message || '-'}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    {log.failed_count > 0 && (
                                      <button 
                                        onClick={() => handleRetryTopIssuerLogFailed(log.id, log.failed_count)} 
                                        disabled={isSyncingTopIssuer} 
                                        className="action-btn btn-danger"
                                        title="Retry hanya PGA yang gagal pada run ini"
                                      >
                                        Retry Gagal
                                      </button>
                                    )}
                                    {log.nodata_count > 0 && (
                                      <button 
                                        onClick={() => handleRetryTopIssuerLogNoData(log.id, log.nodata_count)} 
                                        disabled={isSyncingTopIssuer} 
                                        className="action-btn btn-warning"
                                        title="Retry hanya PGA yang kosong (no data) pada run ini"
                                      >
                                        Retry Kosong
                                      </button>
                                    )}
                                    {!log.failed_count && !log.nodata_count && <span>-</span>}
                                  </div>
                                </td>
                              </tr>
                              {expandedTopIssuerLogId === log.id && (
                                <tr className="expanded-details-row">
                                  <td colSpan="7" style={{ backgroundColor: '#fdfefe', padding: '15px', borderBottom: '1px solid #dee2e6' }}>
                                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                      {log.failed_items && log.failed_items.length > 0 && (
                                        <div style={{ flex: 1, minWidth: '250px' }}>
                                          <h4 style={{ color: '#dc3545', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '600', textAlign: 'left' }}>
                                            🔴 List PGA Gagal ({log.failed_items.length}):
                                          </h4>
                                          <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#fff', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '8px' }}>
                                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#721c24', textAlign: 'left' }}>
                                              {log.failed_items.map((item, idx) => (
                                                <li key={idx} style={{ marginBottom: '4px' }}>
                                                  <strong>{item.pga_url}</strong>
                                                  {item.reason && <span style={{ color: '#888', fontStyle: 'italic', marginLeft: '4px' }}>- {item.reason}</span>}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        </div>
                                      )}
                                      {log.nodata_items && log.nodata_items.length > 0 && (
                                        <div style={{ flex: 1, minWidth: '250px' }}>
                                          <h4 style={{ color: '#856404', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '600', textAlign: 'left' }}>
                                            🟡 List PGA Kosong/No Data ({log.nodata_items.length}):
                                          </h4>
                                          <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#fff', border: '1px solid #ffeeba', borderRadius: '4px', padding: '8px' }}>
                                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#856404', textAlign: 'left' }}>
                                              {log.nodata_items.map((item, idx) => (
                                                <li key={idx} style={{ marginBottom: '4px' }}>
                                                  <strong>{item.pga_url}</strong>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination Controls */}
                    {Math.ceil(topIssuerSyncHistory.length / topIssuerHistoryPerPage) > 1 && (
                      <div className="sync-pagination">
                        <button 
                          className="pagination-btn"
                          disabled={topIssuerHistoryPage === 1}
                          onClick={() => setTopIssuerHistoryPage(prev => prev - 1)}
                        >
                          &laquo; Prev
                        </button>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                          Halaman {topIssuerHistoryPage} dari {Math.ceil(topIssuerSyncHistory.length / topIssuerHistoryPerPage)}
                        </span>
                        <button 
                          className="pagination-btn"
                          disabled={topIssuerHistoryPage === Math.ceil(topIssuerSyncHistory.length / topIssuerHistoryPerPage)}
                          onClick={() => setTopIssuerHistoryPage(prev => prev + 1)}
                        >
                          Next &raquo;
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="sync-modal-footer">
                <button className="sync-button" style={{ background: '#6c757d', padding: '8px 16px', fontSize: '14px' }} onClick={() => setShowTopIssuerHistoryModal(false)}>Tutup</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="sync-section">
        <h2>Sync Transaction Detail (API)</h2>
        <p>Tarik data Transaction Detail dari API <code>stat_daily</code> dan <code>stat_daily_disbursement</code>. Pastikan API Settings sudah dikonfigurasi di Master Data → Master Data API → Global API Settings.</p>
        
        <div className="manual-sync-filters">
          <label>Date Range:</label>
          <input type="date" name="start" value={detailDateRange.start} onChange={handleDetailDateChange} />
          <span>-</span>
          <input type="date" name="end" value={detailDateRange.end} onChange={handleDetailDateChange} />
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={handleSyncDetail} disabled={isSyncingDetail} className="sync-button api-sync-button">
            {isSyncingDetail ? 'Syncing Transaction Detail...' : 'Sync Transaction Detail'}
          </button>
          {detailFailedCount > 0 && !isSyncingDetail && (
            <button onClick={handleRetryFailedDetail} className="sync-button" style={{ background: '#dc3545' }}>
              Retry {detailFailedCount} PGA Gagal
            </button>
          )}

        </div>

        {detailSyncResult && (
          <div className="sync-result-summary" style={{ marginTop: '10px', fontSize: '14px', color: '#555' }}>
            <strong>Hasil terakhir:</strong> {detailSyncResult.message}
            {(detailFailedCount > 0 || detailNoDataCount > 0) && (
              <span style={{ marginLeft: '10px', fontWeight: '600', color: '#dc3545' }}>
                ({detailFailedCount} gagal, {detailNoDataCount} kosong)
              </span>
            )}
          </div>
        )}

        {/* Button to Open Sync History Modal */}
        <div style={{ marginTop: '15px', display: 'flex', gap: '8px' }}>
          <button 
            onClick={() => {
              setShowHistoryModal(true);
              setHistoryPage(1);
              fetchSyncHistory();
            }} 
            className="sync-button" 
            style={{ background: '#17a2b8', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            📋 Lihat Riwayat Sync Detail
          </button>
        </div>

        {/* Sync History Modal Overlay */}
        {showHistoryModal && (
          <div className="sync-modal-overlay" onClick={() => setShowHistoryModal(false)}>
            <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sync-modal-header">
                <h3>Riwayat Sinkronisasi Transaction Detail</h3>
                <button className="sync-modal-close-btn" onClick={() => setShowHistoryModal(false)}>&times;</button>
              </div>
              <div className="sync-modal-body">
                {isLoadingHistory ? (
                  <p style={{ color: '#666', fontSize: '14px', textAlign: 'center', padding: '20px' }}>Memuat riwayat...</p>
                ) : detailSyncHistory.length === 0 ? (
                  <p style={{ color: '#666', fontSize: '14px', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>Belum ada riwayat sinkronisasi detail.</p>
                ) : (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="sync-history-table">
                        <thead>
                          <tr>
                            <th>ID Run</th>
                            <th>Waktu Sync</th>
                            <th>Periode</th>
                            <th>Status</th>
                            <th>Hasil Detail</th>
                            <th>Catatan</th>
                            <th>Aksi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailSyncHistory.slice((historyPage - 1) * historyPerPage, historyPage * historyPerPage).map((log) => (
                            <React.Fragment key={log.id}>
                              <tr>
                                <td>#{log.id}</td>
                                <td>{new Date(log.created_at).toLocaleString('id-ID')}</td>
                                <td>{log.start_date} s/d {log.end_date}</td>
                                <td>
                                  <span className={`status-badge status-${log.status}`}>
                                    {log.status === 'running' ? 'Running' : log.status === 'success' ? 'Sukses' : 'Gagal'}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ fontSize: '13px' }}>
                                    <span style={{ color: '#28a745', fontWeight: 'bold' }} title="Inserted Count">+{log.inserted_count}</span> |{' '}
                                    <span style={{ color: '#007bff', fontWeight: 'bold' }} title="Updated Count">~{log.updated_count}</span> |{' '}
                                    <span style={{ color: '#6c757d', fontWeight: 'bold' }} title="Skipped Count">={log.skipped_count}</span>
                                    {(log.failed_count > 0 || log.nodata_count > 0) && (
                                      <div style={{ marginTop: '4px', fontSize: '12px' }}>
                                        {log.failed_count > 0 && <span style={{ color: '#dc3545', fontWeight: '600', marginRight: '8px' }}>{log.failed_count} Gagal</span>}
                                        {log.nodata_count > 0 && <span style={{ color: '#ffc107', fontWeight: '600' }}>{log.nodata_count} Kosong</span>}
                                        <div style={{ marginTop: '4px' }}>
                                          <button 
                                            onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)} 
                                            style={{
                                              background: 'none', border: 'none', color: '#007bff', padding: 0, fontSize: '11px',
                                              textDecoration: 'underline', cursor: 'pointer', fontWeight: '600'
                                            }}
                                          >
                                            {expandedLogId === log.id ? 'Sembunyikan Detail' : 'Lihat Detail URL'}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td style={{ fontSize: '13px', maxWidth: '200px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {log.message || '-'}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    {log.failed_count > 0 && (
                                      <button 
                                        onClick={() => handleRetryLogFailed(log.id, log.failed_count)} 
                                        disabled={isSyncingDetail} 
                                        className="action-btn btn-danger"
                                        title="Retry hanya PGA yang gagal pada run ini"
                                      >
                                        Retry Gagal
                                      </button>
                                    )}
                                    {log.nodata_count > 0 && (
                                      <button 
                                        onClick={() => handleRetryLogNoData(log.id, log.nodata_count)} 
                                        disabled={isSyncingDetail} 
                                        className="action-btn btn-warning"
                                        title="Retry hanya PGA yang kosong (no data) pada run ini"
                                      >
                                        Retry Kosong
                                      </button>
                                    )}
                                    {!log.failed_count && !log.nodata_count && <span>-</span>}
                                  </div>
                                </td>
                              </tr>
                              {expandedLogId === log.id && (
                                <tr className="expanded-details-row">
                                  <td colSpan="7" style={{ backgroundColor: '#fdfefe', padding: '15px', borderBottom: '1px solid #dee2e6' }}>
                                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                      {log.failed_items && log.failed_items.length > 0 && (
                                        <div style={{ flex: 1, minWidth: '250px' }}>
                                          <h4 style={{ color: '#dc3545', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '600', textAlign: 'left' }}>
                                            🔴 List PGA Gagal ({log.failed_items.length}):
                                          </h4>
                                          <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#fff', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '8px' }}>
                                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#721c24', textAlign: 'left' }}>
                                              {log.failed_items.map((item, idx) => (
                                                <li key={idx} style={{ marginBottom: '4px' }}>
                                                  <strong>{item.pga_url}</strong> ({item.type}) 
                                                  {item.reason && <span style={{ color: '#888', fontStyle: 'italic', marginLeft: '4px' }}>- {item.reason}</span>}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        </div>
                                      )}
                                      {log.nodata_items && log.nodata_items.length > 0 && (
                                        <div style={{ flex: 1, minWidth: '250px' }}>
                                          <h4 style={{ color: '#856404', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '600', textAlign: 'left' }}>
                                            🟡 List PGA Kosong/No Data ({log.nodata_items.length}):
                                          </h4>
                                          <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#fff', border: '1px solid #ffeeba', borderRadius: '4px', padding: '8px' }}>
                                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#856404', textAlign: 'left' }}>
                                              {log.nodata_items.map((item, idx) => (
                                                <li key={idx} style={{ marginBottom: '4px' }}>
                                                  <strong>{item.pga_url}</strong> ({item.type})
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination Controls */}
                    {Math.ceil(detailSyncHistory.length / historyPerPage) > 1 && (
                      <div className="sync-pagination">
                        <button 
                          className="pagination-btn"
                          disabled={historyPage === 1}
                          onClick={() => setHistoryPage(prev => prev - 1)}
                        >
                          &laquo; Prev
                        </button>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                          Halaman {historyPage} dari {Math.ceil(detailSyncHistory.length / historyPerPage)}
                        </span>
                        <button 
                          className="pagination-btn"
                          disabled={historyPage === Math.ceil(detailSyncHistory.length / historyPerPage)}
                          onClick={() => setHistoryPage(prev => prev + 1)}
                        >
                          Next &raquo;
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="sync-modal-footer">
                <button className="sync-button" style={{ background: '#6c757d', padding: '8px 16px', fontSize: '14px' }} onClick={() => setShowHistoryModal(false)}>Tutup</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="sync-section">
        <h2>Sync Merchant Report (Scraping)</h2>
        <p>Tarik data Merchant Receive Total dari report B2B/B2C. Proses ini memakan waktu karena banyak PGA yang di-scrape.</p>
        
        <div className="manual-sync-filters">
          <label>Periode:</label>
          <input type="month" value={merchantMonth} onChange={(e) => setMerchantMonth(e.target.value)} />
          <label style={{ marginLeft: '12px' }}>Type:</label>
          <select value={merchantType} onChange={(e) => setMerchantType(e.target.value)} style={{ padding: '6px 10px' }}>
            <option value="all">Semua (B2B + B2C)</option>
            <option value="B2B">B2B Only</option>
            <option value="B2C">B2C Only</option>
          </select>
          <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={clearExisting} onChange={(e) => setClearExisting(e.target.checked)} />
            <span style={{ color: clearExisting ? '#dc3545' : 'inherit', fontWeight: clearExisting ? '600' : 'normal' }}>
              Hapus data periode ini sebelum sync
            </span>
          </label>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={() => handleSyncMerchant(false)} disabled={isSyncingMerchant} className="sync-button api-sync-button">
            {isSyncingMerchant ? 'Syncing...' : 'Sync All Merchant'}
          </button>
          <button onClick={() => handleSyncMerchant(true)} disabled={isSyncingMerchant} className="sync-button" style={{ background: '#6c757d' }}>
            {isSyncingMerchant ? 'Syncing...' : 'Sync All H+30'}
          </button>
          {merchantFailedCount > 0 && !isSyncingMerchant && (
            <button onClick={handleRetryFailed} className="sync-button" style={{ background: '#dc3545' }}>
              Retry {merchantFailedCount} PGA Gagal
            </button>
          )}
          <button onClick={() => setShowPgaPicker(!showPgaPicker)} disabled={isSyncingMerchant} className="sync-button" style={{ background: '#17a2b8' }}>
            {showPgaPicker ? 'Tutup Pilih PGA' : 'Pilih PGA Tertentu'}
          </button>
          <button 
            onClick={() => {
              setShowMerchantHistoryModal(true);
              setMerchantHistoryPage(1);
              fetchMerchantSyncHistory();
            }} 
            className="sync-button" 
            style={{ background: '#17a2b8', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            📋 Lihat Riwayat Sync Merchant
          </button>
        </div>

        {showPgaPicker && (
          <div style={{ marginTop: '12px', padding: '12px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>Pilih PGA:</strong>
              <button onClick={() => selectAllPgaByType('B2C')} style={{ padding: '3px 10px', fontSize: '12px', background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: '4px', cursor: 'pointer' }}>
                Toggle B2C
              </button>
              <button onClick={() => selectAllPgaByType('B2B')} style={{ padding: '3px 10px', fontSize: '12px', background: '#e3f2fd', border: '1px solid #64b5f6', borderRadius: '4px', cursor: 'pointer' }}>
                Toggle B2B
              </button>
              <button onClick={() => setSelectedPgaIds([])} style={{ padding: '3px 10px', fontSize: '12px', background: '#fff', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>
                Clear
              </button>
              <span style={{ color: '#666', fontSize: '13px' }}>({selectedPgaIds.length} dipilih)</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxHeight: '200px', overflowY: 'auto' }}>
              {pgaList.map(pga => (
                <label key={pga.id} style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', fontSize: '12px',
                  background: selectedPgaIds.includes(pga.id) ? (pga.type === 'B2B' ? '#bbdefb' : '#ffe0b2') : '#fff',
                  border: `1px solid ${selectedPgaIds.includes(pga.id) ? '#1976d2' : '#ddd'}`,
                  borderRadius: '4px', cursor: 'pointer', userSelect: 'none'
                }}>
                  <input type="checkbox" checked={selectedPgaIds.includes(pga.id)} onChange={() => togglePgaSelection(pga.id)} style={{ margin: 0 }} />
                  <span style={{ fontWeight: '500' }}>{pga.pga_name}</span>
                  <span style={{ color: '#888', fontSize: '11px' }}>({pga.type})</span>
                </label>
              ))}
            </div>
            {selectedPgaIds.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <button onClick={() => handleSyncSelectedPga(false)} disabled={isSyncingMerchant} className="sync-button api-sync-button" style={{ fontSize: '13px', padding: '6px 14px' }}>
                  {isSyncingMerchant ? 'Syncing...' : `Sync ${selectedPgaIds.length} PGA`}
                </button>
                <button onClick={() => handleSyncSelectedPga(true)} disabled={isSyncingMerchant} className="sync-button" style={{ background: '#6c757d', fontSize: '13px', padding: '6px 14px' }}>
                  {isSyncingMerchant ? 'Syncing...' : `Sync H+30 ${selectedPgaIds.length} PGA`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Merchant Sync History Modal Overlay */}
        {showMerchantHistoryModal && (
          <div className="sync-modal-overlay" onClick={() => setShowMerchantHistoryModal(false)}>
            <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sync-modal-header">
                <h3>Riwayat Sinkronisasi Merchant Report</h3>
                <button className="sync-modal-close-btn" onClick={() => setShowMerchantHistoryModal(false)}>&times;</button>
              </div>
              <div className="sync-modal-body">
                {isLoadingMerchantHistory ? (
                  <p style={{ color: '#666', fontSize: '14px', textAlign: 'center', padding: '20px' }}>Memuat riwayat...</p>
                ) : merchantSyncHistory.length === 0 ? (
                  <p style={{ color: '#666', fontSize: '14px', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>Belum ada riwayat sinkronisasi merchant.</p>
                ) : (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="sync-history-table">
                         <thead>
                           <tr>
                             <th>ID Run</th>
                             <th>Waktu Sync</th>
                             <th>Periode</th>
                             <th>H+30</th>
                             <th>Status</th>
                             <th>Hasil Sync</th>
                             <th>Catatan</th>
                             <th>Aksi</th>
                           </tr>
                         </thead>
                         <tbody>
                           {merchantSyncHistory.slice((merchantHistoryPage - 1) * merchantHistoryPerPage, merchantHistoryPage * merchantHistoryPerPage).map((log) => (
                             <React.Fragment key={log.id}>
                               <tr>
                                 <td>#{log.id}</td>
                                 <td>{new Date(log.created_at).toLocaleString('id-ID')}</td>
                                 <td>{log.period}</td>
                                 <td>{log.is_h30 ? 'Ya' : 'Tidak'}</td>
                                 <td>
                                   <span className={`status-badge status-${log.status}`}>
                                     {log.status === 'running' ? 'Running' : log.status === 'success' ? 'Sukses' : 'Gagal'}
                                   </span>
                                 </td>
                                 <td>
                                   <div style={{ fontSize: '13px' }}>
                                     <span style={{ color: '#28a745', fontWeight: 'bold' }} title="PGA Berhasil">+{log.success_count}</span> |{' '}
                                     <span style={{ color: '#6c757d', fontWeight: 'bold' }} title="Total Merchants">={log.total_merchants} m</span>
                                     {(log.failed_count > 0 || log.nodata_count > 0) && (
                                       <div style={{ marginTop: '4px', fontSize: '12px' }}>
                                         {log.failed_count > 0 && <span style={{ color: '#dc3545', fontWeight: '600', marginRight: '8px' }}>{log.failed_count} Gagal</span>}
                                         {log.nodata_count > 0 && <span style={{ color: '#ffc107', fontWeight: '600' }}>{log.nodata_count} Kosong</span>}
                                         <div style={{ marginTop: '4px' }}>
                                           <button 
                                             onClick={() => setExpandedMerchantLogId(expandedMerchantLogId === log.id ? null : log.id)} 
                                             style={{
                                               background: 'none', border: 'none', color: '#007bff', padding: 0, fontSize: '11px',
                                               textDecoration: 'underline', cursor: 'pointer', fontWeight: '600'
                                             }}
                                           >
                                             {expandedMerchantLogId === log.id ? 'Sembunyikan Detail' : 'Lihat Detail URL'}
                                           </button>
                                         </div>
                                       </div>
                                     )}
                                   </div>
                                 </td>
                                 <td style={{ fontSize: '13px', maxWidth: '200px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                   {log.message || '-'}
                                 </td>
                                 <td>
                                   <div style={{ display: 'flex', gap: '6px' }}>
                                     {log.failed_count > 0 && (
                                       <button 
                                         onClick={() => handleRetryLogFailedMerchant(log.id, log.failed_count)} 
                                         disabled={isSyncingMerchant} 
                                         className="action-btn btn-danger"
                                         title="Retry hanya PGA yang gagal pada run ini"
                                       >
                                         Retry Gagal
                                       </button>
                                     )}
                                     {log.nodata_count > 0 && (
                                       <button 
                                         onClick={() => handleRetryLogNoDataMerchant(log.id, log.nodata_count)} 
                                         disabled={isSyncingMerchant} 
                                         className="action-btn btn-warning"
                                         title="Retry hanya PGA yang kosong (no data) pada run ini"
                                       >
                                         Retry Kosong
                                       </button>
                                     )}
                                     {!log.failed_count && !log.nodata_count && <span>-</span>}
                                   </div>
                                 </td>
                               </tr>
                               {expandedMerchantLogId === log.id && (
                                 <tr className="expanded-details-row">
                                   <td colSpan="8" style={{ backgroundColor: '#fdfefe', padding: '15px', borderBottom: '1px solid #dee2e6' }}>
                                     <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                       {log.failed_items && log.failed_items.length > 0 && (
                                         <div style={{ flex: 1, minWidth: '250px' }}>
                                           <h4 style={{ color: '#dc3545', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '600', textAlign: 'left' }}>
                                             🔴 List PGA Gagal ({log.failed_items.length}):
                                           </h4>
                                           <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#fff', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '8px' }}>
                                             <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#721c24', textAlign: 'left' }}>
                                               {log.failed_items.map((item, idx) => (
                                                 <li key={idx} style={{ marginBottom: '4px' }}>
                                                   <strong>{item.pga_name}</strong> ({item.type} - Ext ID: {item.pga_id_ext})
                                                   {item.reason && <span style={{ color: '#888', fontStyle: 'italic', marginLeft: '4px' }}>- {item.reason}</span>}
                                                 </li>
                                               ))}
                                             </ul>
                                           </div>
                                         </div>
                                       )}
                                       {log.nodata_items && log.nodata_items.length > 0 && (
                                         <div style={{ flex: 1, minWidth: '250px' }}>
                                           <h4 style={{ color: '#856404', fontSize: '13px', margin: '0 0 8px 0', fontWeight: '600', textAlign: 'left' }}>
                                             🟡 List PGA Kosong/No Data ({log.nodata_items.length}):
                                           </h4>
                                           <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#fff', border: '1px solid #ffeeba', borderRadius: '4px', padding: '8px' }}>
                                             <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#856404', textAlign: 'left' }}>
                                               {log.nodata_items.map((item, idx) => (
                                                 <li key={idx} style={{ marginBottom: '4px' }}>
                                                   <strong>{item.pga_name}</strong> ({item.type} - Ext ID: {item.pga_id_ext})
                                                 </li>
                                               ))}
                                             </ul>
                                           </div>
                                         </div>
                                       )}
                                     </div>
                                   </td>
                                 </tr>
                               )}
                             </React.Fragment>
                           ))}
                         </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination Controls */}
                    {Math.ceil(merchantSyncHistory.length / merchantHistoryPerPage) > 1 && (
                      <div className="sync-pagination">
                        <button 
                          className="pagination-btn"
                          disabled={merchantHistoryPage === 1}
                          onClick={() => setMerchantHistoryPage(prev => prev - 1)}
                        >
                          &laquo; Prev
                        </button>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                          Halaman {merchantHistoryPage} dari {Math.ceil(merchantSyncHistory.length / merchantHistoryPerPage)}
                        </span>
                        <button 
                          className="pagination-btn"
                          disabled={merchantHistoryPage === Math.ceil(merchantSyncHistory.length / merchantHistoryPerPage)}
                          onClick={() => setMerchantHistoryPage(prev => prev + 1)}
                        >
                          Next &raquo;
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="sync-modal-footer">
                <button className="sync-button" style={{ background: '#6c757d', padding: '8px 16px', fontSize: '14px' }} onClick={() => setShowMerchantHistoryModal(false)}>Tutup</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="sync-section">
        <h2>Scheduled Sync</h2>
        <p>Atur sinkronisasi otomatis untuk berjalan secara berkala.</p>
        <div className="settings-form">
          <div className="form-group-toggle">
            <label>Enable Scheduled Sync</label>
            <label className="switch">
              <input type="checkbox" name="is_enabled" checked={settings.is_enabled || false} onChange={handleSettingChange} />
              <span className="slider round"></span>
            </label>
          </div>
          <div className="form-group">
            <label>Run every</label>
            <input type="number" name="interval_days" value={settings.interval_days || 1} onChange={handleSettingChange} min="1" />
            <label>day(s)</label>
          </div>
          <div className="form-group">
            <label>At hour</label>
            <input type="number" name="run_hour" value={settings.run_hour || 0} onChange={handleSettingChange} min="0" max="23" />
            <label>(0-23 UTC)</label>
          </div>
          <button onClick={handleSaveSettings} disabled={isSaving} className="save-button">
            {isSaving ? 'Saving...' : 'Save Schedule'}
          </button>
        </div>
      </div>

      <div className="sync-section">
        <h2>Real-Time System Logs</h2>
        <p>Live logs from journalctl (proyekflask service)</p>
        
        <div className="log-controls">
          <div className="form-group">
            <label>Lines to show:</label>
            <select value={logLines} onChange={(e) => setLogLines(parseInt(e.target.value))}>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
            </select>
          </div>
          
          <div className="form-group-toggle">
            <label>Auto-refresh (3s)</label>
            <label className="switch">
              <input 
                type="checkbox" 
                checked={autoRefresh} 
                onChange={(e) => setAutoRefresh(e.target.checked)} 
              />
              <span className="slider round"></span>
            </label>
          </div>
          
          <button onClick={fetchRealtimeLogs} className="refresh-button">
            🔄 Refresh Now
          </button>
        </div>
        
        <div className="log-container realtime-logs">
          <pre>{realtimeLogs}</pre>
        </div>
      </div>

    </div>
  );
}

export default SyncPage;
